import React, { useState } from "react";
import {
  Box,
  LinearProgress,
  Button,
  Typography,
  FormHelperText,
  Avatar,
  Link,
  InputLabel,
  MenuItem,
  FormControl,
  Checkbox,
  FormControlLabel,
  Grid,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import { Formik, Form, Field } from "formik";
import { TextField, Select } from "formik-material-ui";
import { Redirect } from "react-router-dom";
import * as Yup from "yup";
import classnames from "classnames";
import { postAddUser, patchUpdateUser } from "../../../Api";
import { FormikAutocomplete } from "../../Common";
import { compareStrings } from "../../../Helpers/utils";
import {
  collapseUserLeadSources,
  expandUserLeadSources,
} from "../../../Helpers/users";
import useStyles from "./editUser.style";
import { Alert } from "@material-ui/lab";

interface User {
  id: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  companyName: string;
  leadSource: string[];
  companyLogo: string;
  role: string;
  isActive: boolean;
  inventoryEnabled: boolean;
  financeApplicationUrl: string | null;
  integrationPartner: string | null;
  portalApiAccessEnabled: boolean;
  companyLogoFile?: any;
}

interface EditUserProps {
  vendors: Array<any>;
  user?: User;
  onEditDone: () => void;
}

const EditUser = (props: EditUserProps) => {
  const classes = useStyles();

  const [userUpdated, setUserUpdated] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (userUpdated) {
    return <Redirect to="/admin"></Redirect>;
  }

  const getLogo = (logoFile: any, companyLogo: any) => {
    if (logoFile) return logoFile.name;
    if (companyLogo && props.user)
      return (
        <Avatar
          src={props.user.companyLogo}
          className={classes.companyLogoAvatarImg}
          alt="No Logo"
          variant="square"
          classes={{ img: classes.companyLogoAvatarImg }}
        >
          No Logo
        </Avatar>
      );
    return "Company Logo";
  };
  const title = props.user
    ? "Edit Portal User"
    : "Taycor Admin - Portal User Set Up";

  return (
    <Box className={classes.root}>
      <Box>
        <Typography variant="h1">{title}</Typography>
        <Typography variant="h6" className={classes.headerSubtitle}>
          Here you can make changes or updates to the information associated
          with any of the users associated with your account. If you need any
          assistance you can reach out to us via chat by using the chat icon in
          the lower right corner of your screen. Please note that Company logos
          are limited to PNG or jpeg file types.
        </Typography>
        <Box className={classes.addIcon}>
          <Box>
            <PersonAddIcon></PersonAddIcon>
          </Box>
        </Box>
      </Box>
      <Formik
        initialValues={{
          firstName: props.user?.firstName || "",
          lastName: props.user?.lastName || "",
          emailAddress: props.user?.emailAddress || "",
          companyName: props.user?.companyName || "",
          role: props.user?.role || "User",
          isActive: props.user ? props.user.isActive : true,
          inventoryEnabled: props.user?.inventoryEnabled || false,
          leadSource: expandUserLeadSources(
            props.user?.leadSource || [],
            props.vendors
          ),
          leadSourceShort: props.user?.leadSource || [],
          financeApplicationUrl: props.user?.financeApplicationUrl || "",
          integrationPartner: props.user?.integrationPartner || "None",
          portalApiAccessEnabled: props.user?.portalApiAccessEnabled || false,
          companyLogo: props.user?.companyLogo,
          companyLogoFile: null,
        }}
        validationSchema={Yup.object().shape({
          firstName: Yup.string().required("Required"),
          lastName: Yup.string().required("Required"),
          emailAddress: Yup.string()
            .email("Invalid emaill address")
            .required("Required"),
          companyName: Yup.string().required("Required"),
          leadSource: Yup.string().required("Required"),
          financeApplicationUrl: Yup.string().url(),
        })}
        onSubmit={(values, { setSubmitting }) => {
          const data = {
            firstName: values.firstName,
            lastName: values.lastName,
            emailAddress: values.emailAddress,
            companyName: values.companyName,
            leadSource: values.leadSourceShort,
            companyLogo: values.companyLogo,
            role: values.role,
            isActive: values.isActive,
            inventoryEnabled: values.inventoryEnabled,
            financeApplicationUrl: values.financeApplicationUrl,
            integrationPartner: values.integrationPartner,
            portalApiAccessEnabled: values.portalApiAccessEnabled,
          };

          props.user
            ? patchUpdateUser(
                Object.assign(data, { id: props.user.id }),
                values.companyLogoFile,
                () => {
                  setSubmitting(false);
                  setUserUpdated(true);
                  props.onEditDone();
                },
                (err) => {
                  setSubmitting(false);
                  setShowError(true);
                  setErrorMessage(err?.error || null);
                }
              )
            : postAddUser(
                data,
                values.companyLogoFile,
                () => {
                  setSubmitting(false);
                  setUserUpdated(true);
                  props.onEditDone();
                },
                (err) => {
                  setSubmitting(false);
                  setShowError(true);
                  setErrorMessage(err?.response?.data?.error || null);
                }
              );
        }}
      >
        {({ isSubmitting, setFieldValue, values }) => (
          <Form className={classes.form}>
            <Grid container direction="column" className={classes.grid}>
              <Grid item>
                <Grid container direction="row">
                  <Grid item className={classes.gridItem}>
                    <Field
                      component={TextField}
                      name="firstName"
                      type="text"
                      label="First Name"
                      variant="standard"
                      size="medium"
                      margin="normal"
                      className={classes.formField}
                    ></Field>
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <Field
                      component={TextField}
                      name="lastName"
                      type="text"
                      label="Last Name"
                      variant="standard"
                      size="medium"
                      margin="normal"
                      className={classes.formField}
                    ></Field>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container direction="row">
                  <Grid item className={classes.gridItem}>
                    <Field
                      component={TextField}
                      name="emailAddress"
                      type="text"
                      label="Email Address"
                      variant="standard"
                      size="medium"
                      margin="normal"
                      readOnly={props.user}
                      className={classes.formField}
                    ></Field>
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <Field
                      component={TextField}
                      name="companyName"
                      type="text"
                      label="Company Name"
                      variant="standard"
                      size="medium"
                      margin="normal"
                      className={classes.formField}
                    ></Field>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container direction="row">
                  <Grid item className={classes.gridItem}>
                    <Field
                      component={FormikAutocomplete}
                      options={props.vendors
                        .map((v) => v.name)
                        .sort(compareStrings)}
                      name="leadSource"
                      textFieldProps={{
                        type: "text",
                        variant: "standard",
                        size: "medium",
                        margin: "normal",
                        label: "Lead Source",
                      }}
                      onChange={(_: any, value: any) => {
                        const newValues = expandUserLeadSources(
                          value,
                          props.vendors,
                          [...values.leadSource]
                        );
                        setFieldValue("leadSource", newValues);
                        setFieldValue(
                          "leadSourceShort",
                          collapseUserLeadSources(newValues)
                        );
                      }}
                      className={classes.formField}
                    ></Field>
                    <FormHelperText>
                      <Typography
                        variant="body2"
                        className={classes.helperText}
                        component="span"
                      >
                        SQL Compatible CSV List in Quotes
                      </Typography>
                    </FormHelperText>
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <Field
                      component="div"
                      aria-describedby="companyLogoHelperText"
                      className={classes.logoFormField}
                    >
                      <Button
                        component="label"
                        variant="text"
                        endIcon={<AddIcon></AddIcon>}
                        className={classes.upload}
                      >
                        {getLogo(values.companyLogoFile, values.companyLogo)}
                        <input
                          id="inputCompanyLogoFile"
                          name="inputCompanyLogoFile"
                          type="file"
                          onChange={(event: any) => {
                            setFieldValue(
                              "companyLogoFile",
                              event.currentTarget.files[0]
                            );
                          }}
                          style={{ display: "none" }}
                          accept="image/png, image/jpeg"
                        />
                      </Button>
                      {(values.companyLogo || values.companyLogoFile) && (
                        <Link
                          variant="body2"
                          onClick={() => {
                            setFieldValue("companyLogo", null);
                            setFieldValue("companyLogoFile", null);
                          }}
                          className={classes.removeLogoLink}
                        >
                          remove
                        </Link>
                      )}
                    </Field>
                    <FormHelperText
                      id="companyLogoHelperText"
                      className={classes.companyLogoHelperText}
                    >
                      <Typography
                        variant="body2"
                        className={classes.helperText}
                        component="span"
                      >
                        Restrictions File Format to JPG (Family), .PNG
                      </Typography>
                    </FormHelperText>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container direction="row">
                  <Grid item className={classes.gridItem}>
                    <FormControl
                      variant="standard"
                      size="medium"
                      margin="normal"
                      className={classes.formField}
                    >
                      <InputLabel id="labelSelectRole">User Type</InputLabel>
                      <Field
                        component={Select}
                        name="role"
                        labelId="labelSelectRole"
                        label="User Type"
                      >
                        <MenuItem value="User">User</MenuItem>
                        <MenuItem value="Admin">Admin</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.isActive}
                          onChange={(e) =>
                            setFieldValue("isActive", e.target.checked)
                          }
                          name="isActive"
                          color="primary"
                        />
                      }
                      label="Is Active"
                      className={classes.isActive}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container direction="row">
                  <Grid item className={classes.gridItem}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.inventoryEnabled}
                          onChange={(e) =>
                            setFieldValue("inventoryEnabled", e.target.checked)
                          }
                          name="inventoryEnabled"
                          color="primary"
                        />
                      }
                      label="Inventory Enabled"
                      className={classes.isActive}
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <Field
                      component={TextField}
                      name="financeApplicationUrl"
                      type="text"
                      label="Finance Application Url"
                      variant="standard"
                      size="medium"
                      margin="normal"
                      readOnly={props.user}
                      className={classes.formField}
                    ></Field>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container direction="row">
                  <Grid item className={classes.gridItem}>
                    <FormControl
                      variant="standard"
                      size="medium"
                      margin="normal"
                      className={classes.formField}
                    >
                      <InputLabel id="labelSelectPartmer">Partner</InputLabel>
                      <Field
                        component={Select}
                        name="integrationPartner"
                        labelId="labelIntegrationPartner"
                        label="User Type"
                      >
                        <MenuItem value="None">None</MenuItem>
                        <MenuItem value="Soarr">Soarr</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.portalApiAccessEnabled}
                          onChange={(e) =>
                            setFieldValue(
                              "portalApiAccessEnabled",
                              e.target.checked
                            )
                          }
                          name="portalApiAccessEnabled"
                          color="primary"
                        />
                      }
                      label="Portal API access enabled"
                      className={classes.isActive}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            {isSubmitting && <LinearProgress />}
            <Box className={classes.actions}>
              <Button
                variant="contained"
                type="submit"
                color="primary"
                disabled={isSubmitting}
                className={classes.submit}
              >
                Submit
              </Button>
            </Box>
            <Box
              className={classnames(classes.alert, {
                [classes.visible]: showError,
                [classes.hidden]: !showError,
              })}
            >
              <Alert severity="error">
                <Typography variant="h6">
                  {errorMessage || "Something went wrong, please try again"}
                </Typography>
              </Alert>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default EditUser;
